// @flow

import React, { type Node, useMemo } from 'react';
import styled from 'styled-components';
import lightbulbSrc from './lightbulb-icon.svg';
import { Separator } from '../../atoms';

type Props = {|
  icon?: string,
  iconAlt?: string,
  children: Node,
  separatorSize?: number,
  fillBackground?: boolean,
|};

export function InformationBox({ icon, iconAlt, children, separatorSize = 5, fillBackground = false }: Props): Node {
  const iconElement = useMemo(() =>
    typeof icon === 'string'
      ? fillBackground === false
        ? <CustomIconHolder><img src={icon} alt={iconAlt} /></CustomIconHolder>
        : <DefaultIconHolder><img src={icon} alt={iconAlt} /></DefaultIconHolder>
      : <DefaultIconHolder><img src={lightbulbSrc} alt="light bulb icon" /></DefaultIconHolder>
  , [icon]);

  return (
    <Root>
      <IconElementWrapper>
        {iconElement}
      </IconElementWrapper>
      <Separator direction="h" size={separatorSize} />
      <ChildrenWrapper>
        {children}
      </ChildrenWrapper>
    </Root>
  );
}

const Root = styled.div`
  background-color: ${props => props.theme.colors.new_primary[50]};
  color: ${props => props.theme.colors.neutral[900]};
  display: flex;
  align-items: flex-start;
  padding: 12px 20px;
`;

const IconElementWrapper = styled.div`
  margin-top: 0.25em;
`;

const ChildrenWrapper = styled.div`
  font-size: 12px;
  font-family: ${props => props.theme.fontFamily.sansSerif};
`;

const DefaultIconHolder = styled.div`
  background: ${props => props.theme.colors.primary(40)};
  width: auto;
  min-width: 32px;
  height: 32px;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;

  & > img {
    width: auto;
    height: 100%;
  }
`;

const CustomIconHolder = styled(DefaultIconHolder)`
  background: none;
`;
